import React from 'react';

const colorMap = {
  green: 'bg-green-500',
  red: 'bg-red-500',
  yellow: 'bg-yellow-500',
  gray: 'bg-gray-400',
  blue: 'bg-primary-500',
  orange: 'bg-orange-500',
  purple: 'bg-purple-500',
} as const;

// Dots scale up one step at wall-display widths (3xl = 1920px) so live-status
// pulses stay visible from across an EOC.
const sizeMap = {
  xs: 'h-1.5 w-1.5 3xl:h-2 3xl:w-2',
  sm: 'h-2 w-2 3xl:h-2.5 3xl:w-2.5',
  md: 'h-3 w-3 3xl:h-4 3xl:w-4',
  lg: 'h-4 w-4 3xl:h-5 3xl:w-5',
} as const;

export type StatusDotColor = keyof typeof colorMap;
export type StatusDotSize = keyof typeof sizeMap;

interface StatusDotProps {
  color: StatusDotColor;
  size?: StatusDotSize;
  pulse?: boolean;
  label?: string;
  className?: string;
}

const StatusDot: React.FC<StatusDotProps> = ({
  color,
  size = 'sm',
  pulse = false,
  label,
  className = '',
}) => {
  return (
    <span
      className={`inline-block rounded-full ${colorMap[color]} ${sizeMap[size]} ${pulse ? 'animate-pulse' : ''} ${className}`.trim()}
      title={label}
      // A bare <span> may not carry aria-label (axe: aria-prohibited-attr), so a
      // labelled dot is exposed as an image; an unlabelled one is decorative and
      // hidden, because its meaning must then be carried by adjacent text
      // (WCAG 1.4.1: never colour alone).
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
    />
  );
};

export default StatusDot;
